import { lazy, Suspense, useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { refreshAttributionFromSearchParams } from './lib/attributionSession.js'
import { useFunnelTracker } from './lib/useFunnelTracker.js'
import { ModalProvider } from './contexts/ModalContext.jsx'
import { ScrollToTop } from './components/ScrollToTop.jsx'
import { HomePage } from './pages/home/HomePage.jsx'

// Every route but home is split into its own chunk, so the first visit only
// downloads the home page (plus shared code) instead of the whole site —
// checkout, intake, blog, etc. load when navigated to.
const IntakePage = lazy(() => import('./pages/intake/IntakePage.jsx').then((m) => ({ default: m.IntakePage })))
const CheckoutPage = lazy(() => import('./pages/CheckoutPage.jsx').then((m) => ({ default: m.CheckoutPage })))
const AboutPage = lazy(() => import('./pages/about/AboutPage.jsx').then((m) => ({ default: m.AboutPage })))
const PricingPage = lazy(() => import('./pages/pricing/PricingPage.jsx').then((m) => ({ default: m.PricingPage })))
const ProvidersPage = lazy(() => import('./pages/providers/ProvidersPage.jsx').then((m) => ({ default: m.ProvidersPage })))
const LabsPage = lazy(() => import('./pages/labs/LabsPage.jsx').then((m) => ({ default: m.LabsPage })))
const BlogIndexPage = lazy(() => import('./pages/blog/BlogIndexPage.jsx').then((m) => ({ default: m.BlogIndexPage })))
const BlogPostPage = lazy(() => import('./pages/blog/BlogPostPage.jsx').then((m) => ({ default: m.BlogPostPage })))
const ProductsPage = lazy(() => import('./pages/products/ProductsPage.jsx').then((m) => ({ default: m.ProductsPage })))
const WeightLossPage = lazy(() => import('./pages/services/WeightLossPage.jsx').then((m) => ({ default: m.WeightLossPage })))
const WomensHormonesPage = lazy(() => import('./pages/services/WomensHormonesPage.jsx').then((m) => ({ default: m.WomensHormonesPage })))
const MensHealthPage = lazy(() => import('./pages/services/MensHealthPage.jsx').then((m) => ({ default: m.MensHealthPage })))
const PeptidesPage = lazy(() => import('./pages/services/PeptidesPage.jsx').then((m) => ({ default: m.PeptidesPage })))
const WellnessPage = lazy(() => import('./pages/services/WellnessPage.jsx').then((m) => ({ default: m.WellnessPage })))
const RecoveryPage = lazy(() => import('./pages/services/RecoveryPage.jsx').then((m) => ({ default: m.RecoveryPage })))
const UrgentCarePage = lazy(() => import('./pages/services/UrgentCarePage.jsx').then((m) => ({ default: m.UrgentCarePage })))
const FaqPage = lazy(() => import('./pages/legal/FaqPage.jsx').then((m) => ({ default: m.FaqPage })))
const ContactPage = lazy(() => import('./pages/legal/ContactPage.jsx').then((m) => ({ default: m.ContactPage })))
const PrivacyPage = lazy(() => import('./pages/legal/PrivacyPage.jsx').then((m) => ({ default: m.PrivacyPage })))
const TermsPage = lazy(() => import('./pages/legal/TermsPage.jsx').then((m) => ({ default: m.TermsPage })))

/**
 * Records an affiliate last-touch on every navigation.
 *
 * Mounted inside the router so it sees client-side route changes, not just the
 * initial load — an affiliate link that lands on `/` and then navigates to
 * `/intake` must still be captured.
 *
 * Only affiliate-medium UTMs are stored, and the cookie is an *input* to
 * server-side attribution, never a verdict. See lib/attributionSession.js.
 */
function AttributionCapture() {
  const location = useLocation()
  useEffect(() => {
    refreshAttributionFromSearchParams(location.search)
  }, [location.search])
  return null
}

function FunnelCapture() {
  useFunnelTracker()
  return null
}

/** Rendered inside a router: BrowserRouter in main.jsx, StaticRouter in entry-server.jsx. */
function App() {
  return (
    <>
      <AttributionCapture />
      <FunnelCapture />
      <ScrollToTop />
      <ModalProvider>
        <Suspense fallback={<div className="min-h-screen" />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/intake" element={<IntakePage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/providers" element={<ProvidersPage />} />
            <Route path="/labs" element={<LabsPage />} />
            <Route path="/blog" element={<BlogIndexPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/weight-loss" element={<WeightLossPage />} />
            <Route path="/womens-hormones" element={<WomensHormonesPage />} />
            <Route path="/mens-health" element={<MensHealthPage />} />
            <Route path="/peptides" element={<PeptidesPage />} />
            <Route path="/longevity" element={<Navigate to="/wellness" replace />} />
            <Route path="/wellness" element={<WellnessPage />} />
            <Route path="/recovery" element={<RecoveryPage />} />
            <Route path="/urgent-care" element={<UrgentCarePage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
          </Routes>
        </Suspense>
      </ModalProvider>
    </>
  )
}

export default App
